import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { it, hrms } from '../../services/api';
import Card from '../../components/common/Card';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { theme } from '../../theme';
import { useAuth } from '../../contexts/AuthContext';

const inp = { width: '100%', padding: '0.625rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, background: theme.white, outline: 'none', boxSizing: 'border-box' };
const lbl = { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: theme.text, marginBottom: '0.4rem' };

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', padding: '0.625rem 0', borderBottom: `1px solid ${theme.border}`, minWidth: 0 }}>
      <span style={{ width: '110px', minWidth: '110px', fontSize: '0.8rem', color: theme.textMuted, fontWeight: 500, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: '0.875rem', color: theme.text, fontWeight: 500, minWidth: 0, wordBreak: 'break-word', flex: 1 }}>{value || '—'}</span>
    </div>
  );
}

function SpecTile({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ background: theme.pageBg, borderRadius: '0.5rem', padding: '0.625rem 0.875rem', border: `1px solid ${theme.border}` }}>
      <p style={{ fontSize: '0.72rem', color: theme.textMuted, fontWeight: 600, margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</p>
      <p style={{ fontSize: '0.875rem', fontWeight: 600, color: theme.text, margin: '0.25rem 0 0' }}>{String(value)}</p>
    </div>
  );
}

function HealthGauge({ score, label, color }) {
  const radius = 36;
  const stroke = 7;
  const norm = radius - stroke / 2;
  const circ = 2 * Math.PI * norm;
  const dash = (score / 100) * circ;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
      <svg width={radius * 2 + stroke} height={radius * 2 + stroke} viewBox={`0 0 ${radius * 2 + stroke} ${radius * 2 + stroke}`}>
        <circle cx={radius + stroke / 2} cy={radius + stroke / 2} r={norm} fill="none" stroke={theme.border} strokeWidth={stroke} />
        <circle cx={radius + stroke / 2} cy={radius + stroke / 2} r={norm} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          transform={`rotate(-90 ${radius + stroke / 2} ${radius + stroke / 2})`} />
        <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" fontSize="14" fontWeight="700" fill={color}>{score}</text>
      </svg>
      <span style={{ fontSize: '0.75rem', fontWeight: 600, color, textAlign: 'center' }}>{label}</span>
    </div>
  );
}

const today = () => new Date().toISOString().slice(0, 10);

function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ['it_admin', 'super_admin', 'cfo', 'cto'].includes(user?.role);

  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [tickets, setTickets] = useState([]);

  // QR
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [showQR, setShowQR] = useState(false);

  // Action modals
  const [modal, setModal] = useState(null); // 'assign' | 'return' | 'transfer'
  const [actionForm, setActionForm] = useState({});
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  const loadAsset = useCallback(() => {
    it.getAsset(id).then((r) => { setAsset(r.data); setLoading(false); }).catch(() => setLoading(false));
    it.getTickets({ asset_id: id }).then((r) => setTickets(r.data || [])).catch(() => {});
  }, [id]);

  useEffect(() => {
    loadAsset();
    if (isAdmin) {
      hrms.getEmployees({}).then((r) => setEmployees(r.data || [])).catch(err => console.warn('[HRMS]', err?.message));
    }
  }, [loadAsset, isAdmin]);

  const openModal = (type) => {
    setActionError('');
    setActionForm(
      type === 'assign' ? { employee_id: '', assigned_date: today() } :
      type === 'return' ? { return_date: today(), return_reason: '' } :
      { to_employee_id: '', transfer_date: today(), reason: '' }
    );
    setModal(type);
  };

  const handleAction = async () => {
    setActionError('');
    setActionLoading(true);
    try {
      if (modal === 'assign') {
        if (!actionForm.employee_id) { setActionError('Select an employee.'); setActionLoading(false); return; }
        await it.assignAsset(id, { employee_id: Number(actionForm.employee_id), assigned_date: actionForm.assigned_date });
      } else if (modal === 'return') {
        await it.returnAsset(id, { return_date: actionForm.return_date, return_reason: actionForm.return_reason || null });
      } else if (modal === 'transfer') {
        if (!actionForm.to_employee_id) { setActionError('Select a target employee.'); setActionLoading(false); return; }
        await it.transferAsset(id, { to_employee_id: Number(actionForm.to_employee_id), transfer_date: actionForm.transfer_date, reason: actionForm.reason || null });
      }
      setModal(null);
      loadAsset();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setActionError(typeof detail === 'string' ? detail : 'Action failed.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleShowQR = async () => {
    if (!qrData) {
      setQrLoading(true);
      try { const r = await it.getAssetQR(id); setQrData(r.data); } catch { } finally { setQrLoading(false); }
    }
    setShowQR(true);
  };

  const handlePrintQR = () => {
    const w = window.open('', '_blank');
    const style = w.document.createElement('style');
    style.textContent = 'body{font-family:sans-serif;text-align:center;padding:2rem} img{width:200px;height:200px} p{margin:0.5rem 0;font-size:0.9rem}';
    w.document.head.appendChild(style);
    w.document.title = `QR – ${asset.asset_code}`;
    const img = w.document.createElement('img');
    img.src = `data:image/png;base64,${qrData.qr_base64}`;
    const p1 = w.document.createElement('p');
    p1.innerHTML = `<strong>${asset.asset_code}</strong>`;
    const p2 = w.document.createElement('p');
    p2.textContent = asset.name;
    const p3 = w.document.createElement('p');
    p3.style.fontSize = '0.75rem'; p3.style.color = '#888';
    p3.textContent = asset.serial_number || '';
    w.document.body.append(img, p1, p2, p3);
    w.print();
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '3rem', color: theme.textMuted }}>Loading...</div>;
  if (!asset) return <p>Asset not found.</p>;

  const status = asset.status || 'available';
  const history = asset.assignment_history || [];
  const hasSpecs = asset.cpu || asset.ram_gb || asset.storage_gb || asset.os || asset.screen_size;
  let customSpecs = null;
  if (asset.custom_specs) {
    try { customSpecs = JSON.parse(asset.custom_specs); } catch { customSpecs = { Notes: asset.custom_specs }; }
  }
  const warrantyDays = asset.warranty_expiry
    ? Math.ceil((new Date(asset.warranty_expiry) - new Date()) / 86400000)
    : null;

  const empOptions = employees.filter((e) => {
    if (modal === 'transfer' && asset.current_assignment) {
      return e.id !== asset.current_assignment.employee_id;
    }
    return true;
  });

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>{asset.name}</h1>
            <Badge status={status} />
            {asset.asset_code && (
              <code style={{ background: theme.pageBg, padding: '0.2rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem', border: `1px solid ${theme.border}` }}>{asset.asset_code}</code>
            )}
          </div>
          {asset.make && asset.model && (
            <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.35rem 0 0' }}>{asset.make} · {asset.model}</p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {isAdmin && status === 'available' && (
            <Button variant="primary" onClick={() => openModal('assign')}>Assign</Button>
          )}
          {isAdmin && status === 'assigned' && (
            <>
              <Button variant="outline" onClick={() => openModal('transfer')}>Transfer</Button>
              <Button variant="outlineGray" onClick={() => openModal('return')}>Return</Button>
            </>
          )}
          <Button variant="outlineGray" onClick={handleShowQR} loading={qrLoading}>QR Code</Button>
          <Button variant="outline" onClick={() => navigate(`/it/assets/${id}/edit`)}>Edit</Button>
          <Button variant="outlineGray" onClick={() => navigate('/it/assets')}>← Back</Button>
        </div>
      </div>

      {/* Two-column layout: main content + sidebar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 300px', gap: '1.5rem', alignItems: 'start' }}>

        {/* ── Left: main content ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

          {/* Asset Details */}
          <Card title="Asset Details">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0' }}>
              <div>
                <InfoRow label="Asset Code" value={asset.asset_code} />
                <InfoRow label="Category" value={asset.category_name} />
                <InfoRow label="Make / Brand" value={asset.make} />
              </div>
              <div>
                <InfoRow label="Model" value={asset.model} />
                <InfoRow label="Serial Number" value={asset.serial_number} />
                <InfoRow label="Location" value={asset.location} />
              </div>
            </div>
          </Card>

          {/* Technical Specs */}
          {(hasSpecs || customSpecs) && (
            <Card title="Technical Specifications">
              {hasSpecs && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: customSpecs ? '1rem' : 0 }}>
                  <SpecTile label="Processor / CPU" value={asset.cpu} />
                  <SpecTile label="RAM" value={asset.ram_gb ? `${asset.ram_gb} GB` : null} />
                  <SpecTile label="Storage" value={asset.storage_gb ? `${asset.storage_gb} GB` : null} />
                  <SpecTile label="Operating System" value={asset.os} />
                  <SpecTile label="Screen Size" value={asset.screen_size} />
                </div>
              )}
              {customSpecs && (
                <>
                  <p style={{ fontSize: '0.75rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 0.75rem' }}>Additional Specs</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.75rem' }}>
                    {Object.entries(customSpecs).map(([k, v]) => <SpecTile key={k} label={k} value={String(v)} />)}
                  </div>
                </>
              )}
            </Card>
          )}

          {/* Notes */}
          {asset.notes && (
            <Card title="Notes">
              <p style={{ margin: 0, color: theme.text, lineHeight: 1.6, fontSize: '0.875rem' }}>{asset.notes}</p>
            </Card>
          )}

          {/* Ticket History */}
          <Card title={`Ticket History (${tickets.length})`}>
            {tickets.length === 0 ? (
              <p style={{ textAlign: 'center', color: theme.textMuted, padding: '2rem 0', fontSize: '0.875rem' }}>No tickets raised for this asset</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                  <thead>
                    <tr style={{ background: '#F8FAFC' }}>
                      {['Ticket', 'Title', 'Category', 'Priority', 'Status', 'Raised By', 'Assigned To', 'Created', 'Resolved'].map(h => (
                        <th key={h} style={{ padding: '0.6rem 0.875rem', textAlign: 'left', fontWeight: 600, color: theme.textMuted, borderBottom: `2px solid ${theme.border}`, fontSize: '0.72rem', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map((t) => (
                      <tr key={t.id} onClick={() => navigate(`/it/tickets/${t.id}`)}
                        style={{ borderBottom: `1px solid ${theme.border}`, cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}>
                        <td style={{ padding: '0.65rem 0.875rem', whiteSpace: 'nowrap' }}>
                          <code style={{ background: theme.pageBg, padding: '0.15rem 0.4rem', borderRadius: '0.25rem', fontSize: '0.75rem', border: `1px solid ${theme.border}` }}>{t.ticket_code}</code>
                        </td>
                        <td style={{ padding: '0.65rem 0.875rem', fontWeight: 500, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</td>
                        <td style={{ padding: '0.65rem 0.875rem', color: theme.textMuted, textTransform: 'capitalize' }}>{t.category || '—'}</td>
                        <td style={{ padding: '0.65rem 0.875rem' }}><Badge status={t.priority || 'medium'} /></td>
                        <td style={{ padding: '0.65rem 0.875rem' }}><Badge status={t.status || 'open'} /></td>
                        <td style={{ padding: '0.65rem 0.875rem', color: theme.textMuted }}>{t.raised_by_name || '—'}</td>
                        <td style={{ padding: '0.65rem 0.875rem', color: theme.textMuted }}>{t.assignee_name || 'Unassigned'}</td>
                        <td style={{ padding: '0.65rem 0.875rem', color: theme.textMuted, whiteSpace: 'nowrap' }}>{t.created_at ? new Date(t.created_at).toLocaleDateString('en-IN') : '—'}</td>
                        <td style={{ padding: '0.65rem 0.875rem', color: t.resolved_at ? theme.success : theme.textMuted, whiteSpace: 'nowrap' }}>{t.resolved_at ? new Date(t.resolved_at).toLocaleDateString('en-IN') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Assignment History */}
          <Card title="Assignment History">
            {history.length === 0 ? (
              <p style={{ textAlign: 'center', color: theme.textMuted, padding: '2rem 0' }}>No assignment history</p>
            ) : (
              <div style={{ position: 'relative', paddingLeft: '1.5rem' }}>
                <div style={{ position: 'absolute', left: '0.5rem', top: 0, bottom: 0, width: '2px', background: theme.border }} />
                {history.map((h, i) => (
                  <div key={i} style={{ position: 'relative', paddingBottom: '1.25rem' }}>
                    <div style={{ position: 'absolute', left: '-1rem', top: '0.35rem', width: '10px', height: '10px', borderRadius: '50%', background: h.action === 'assigned' ? theme.success : h.action === 'transferred' ? theme.info : theme.warning, border: `2px solid ${theme.white}` }} />
                    <p style={{ fontWeight: 600, fontSize: '0.875rem', margin: 0 }}>
                      {h.action === 'assigned' ? 'Assigned to' : h.action === 'returned' ? 'Returned by' : 'Transferred to'}{' '}
                      <strong>{h.employee_name}</strong>
                    </p>
                    <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: '0.2rem 0 0' }}>
                      {h.date ? new Date(h.date).toLocaleDateString() : '—'}
                      {h.notes && ` · ${h.notes}`}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* ── Right: sidebar ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

          {/* Health / Warranty / Grade */}
          {(asset.health_score != null || warrantyDays != null || asset.inspection_grade) && (
            <Card>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                {asset.health_score != null && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <HealthGauge score={asset.health_score} label={asset.health_label || 'Health'} color={asset.health_color || theme.primary} />
                    {asset.inspection_grade && (
                      <div>
                        <p style={{ fontSize: '0.72rem', color: theme.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 0.2rem' }}>Grade</p>
                        <p style={{ fontSize: '1.75rem', fontWeight: 800, color: theme.primary, margin: 0, lineHeight: 1 }}>{asset.inspection_grade}</p>
                        <p style={{ fontSize: '0.72rem', color: theme.textMuted, margin: '0.1rem 0 0' }}>
                          {{ A: 'Excellent', B: 'Good', C: 'Fair', D: 'Poor' }[asset.inspection_grade] || ''}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                {warrantyDays != null && (
                  <div style={{ borderTop: `1px solid ${theme.border}`, paddingTop: '0.875rem' }}>
                    <p style={{ fontSize: '0.72rem', color: theme.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 0.3rem' }}>Warranty</p>
                    {warrantyDays > 0 ? (
                      <p style={{ fontSize: '1rem', fontWeight: 700, color: warrantyDays < 90 ? theme.warning : theme.success, margin: 0 }}>{warrantyDays} days left</p>
                    ) : (
                      <p style={{ fontSize: '1rem', fontWeight: 700, color: theme.danger, margin: 0 }}>Expired</p>
                    )}
                    <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: '0.15rem 0 0' }}>{new Date(asset.warranty_expiry).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Assignment & Purchase */}
          <Card title="Assignment & Purchase">
            <InfoRow label="Assigned To" value={asset.current_assignment?.employee_name} />
            <InfoRow label="Assigned Date" value={asset.current_assignment?.assigned_date ? new Date(asset.current_assignment.assigned_date).toLocaleDateString() : null} />
            <InfoRow label="Purchase Date" value={asset.purchase_date ? new Date(asset.purchase_date).toLocaleDateString() : null} />
            <InfoRow label="Purchase Cost" value={asset.purchase_cost ? `₹${Number(asset.purchase_cost).toLocaleString()}` : null} />
            <InfoRow label="Warranty Expiry" value={asset.warranty_expiry ? new Date(asset.warranty_expiry).toLocaleDateString() : null} />
          </Card>

        </div>
      </div>

      {/* ── Assign Modal ─────────────────────── */}
      {modal === 'assign' && (
        <Modal title="Assign Asset" onClose={() => setModal(null)} width="400px">
          {actionError && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '0.5rem', padding: '0.65rem 0.875rem', marginBottom: '1rem', color: theme.danger, fontSize: '0.85rem' }}>⚠️ {actionError}</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={lbl}>Employee *</label>
              <select style={inp} value={actionForm.employee_id} onChange={(e) => setActionForm((f) => ({ ...f, employee_id: e.target.value }))}>
                <option value="">Select employee</option>
                {empOptions.map((e) => <option key={e.id} value={e.id}>{e.full_name} {e.employee_id ? `(${e.employee_id})` : ''}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Assigned Date</label>
              <input style={inp} type="date" value={actionForm.assigned_date} onChange={(e) => setActionForm((f) => ({ ...f, assigned_date: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <Button variant="outlineGray" onClick={() => setModal(null)}>Cancel</Button>
              <Button variant="primary" loading={actionLoading} onClick={handleAction}>Assign</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Return Modal ─────────────────────── */}
      {modal === 'return' && (
        <Modal title="Return Asset" onClose={() => setModal(null)} width="400px">
          {actionError && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '0.5rem', padding: '0.65rem 0.875rem', marginBottom: '1rem', color: theme.danger, fontSize: '0.85rem' }}>⚠️ {actionError}</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={lbl}>Return Date</label>
              <input style={inp} type="date" value={actionForm.return_date} onChange={(e) => setActionForm((f) => ({ ...f, return_date: e.target.value }))} />
            </div>
            <div>
              <label style={lbl}>Reason (optional)</label>
              <textarea style={{ ...inp, resize: 'vertical', minHeight: '70px' }} value={actionForm.return_reason} onChange={(e) => setActionForm((f) => ({ ...f, return_reason: e.target.value }))} placeholder="Why is the asset being returned?" />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <Button variant="outlineGray" onClick={() => setModal(null)}>Cancel</Button>
              <Button variant="primary" loading={actionLoading} onClick={handleAction}>Confirm Return</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Transfer Modal ───────────────────── */}
      {modal === 'transfer' && (
        <Modal title="Transfer Asset" onClose={() => setModal(null)} width="400px">
          {actionError && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '0.5rem', padding: '0.65rem 0.875rem', marginBottom: '1rem', color: theme.danger, fontSize: '0.85rem' }}>⚠️ {actionError}</div>}
          {asset.current_assignment && (
            <div style={{ background: theme.pageBg, borderRadius: '0.5rem', padding: '0.625rem 0.875rem', marginBottom: '1rem', border: `1px solid ${theme.border}`, fontSize: '0.85rem', color: theme.textMuted }}>
              Currently assigned to: <strong style={{ color: theme.text }}>{asset.current_assignment.employee_name}</strong>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={lbl}>Transfer To *</label>
              <select style={inp} value={actionForm.to_employee_id} onChange={(e) => setActionForm((f) => ({ ...f, to_employee_id: e.target.value }))}>
                <option value="">Select employee</option>
                {empOptions.map((e) => <option key={e.id} value={e.id}>{e.full_name} {e.employee_id ? `(${e.employee_id})` : ''}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Transfer Date</label>
              <input style={inp} type="date" value={actionForm.transfer_date} onChange={(e) => setActionForm((f) => ({ ...f, transfer_date: e.target.value }))} />
            </div>
            <div>
              <label style={lbl}>Reason (optional)</label>
              <textarea style={{ ...inp, resize: 'vertical', minHeight: '70px' }} value={actionForm.reason} onChange={(e) => setActionForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Reason for transfer..." />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <Button variant="outlineGray" onClick={() => setModal(null)}>Cancel</Button>
              <Button variant="primary" loading={actionLoading} onClick={handleAction}>Transfer</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── QR Modal ─────────────────────────── */}
      {showQR && (
        <Modal title={`QR Code — ${asset.asset_code}`} onClose={() => setShowQR(false)} width="320px">
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            {qrData ? (
              <>
                <img src={`data:image/png;base64,${qrData.qr_base64}`} alt="QR Code" style={{ width: '200px', height: '200px', imageRendering: 'pixelated' }} />
                <p style={{ fontSize: '0.8rem', color: theme.textMuted, marginTop: '0.75rem', fontFamily: 'monospace' }}>{qrData.qr_data}</p>
                <Button variant="primary" onClick={handlePrintQR} style={{ marginTop: '0.75rem' }}>Print QR</Button>
              </>
            ) : (
              <p style={{ color: theme.textMuted }}>Failed to load QR code.</p>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

export default AssetDetail;
